const privacyPolicy =`
# Privacy Policy

**Last Updated: January 19, 2026**

ULLR Maps Ltd ("ULLR Maps," "we," "us," or "our") operates the ULLR Maps website (ullrmaps.com) and the ULLR Maps mobile application. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.

Please read this Privacy Policy carefully. By using our services, you agree to the collection and use of information in accordance with this policy.

---

## 1. Information We Collect

We collect different types of information depending on how you interact with our services.

### 1.1 Mobile Application (No Account Required)

When you use our mobile app, we automatically collect:

- **Device Identifiers**: A unique identifier for your device to distinguish users for analytics purposes
- **Location Information**: Your precise geographic location (GPS coordinates) when you grant location permissions, used to show your position on resort maps
- **Usage Analytics**: How you interact with the app, including features used, screens viewed, and app performance data
- **Device Information**: Device type, operating system version, and app version
- **Crash Reports**: Technical information about app errors to help us improve stability

### 1.2 Web Application (Account Required)

When you create an account on our website, we collect:

- **Account Information**: Email address, username, and password (passwords are encrypted and never stored in plain text)
- **Saved Features**: Locations, favorites, and preferences you choose to save
- **Usage Data**: Pages visited, features used, and interaction patterns

### 1.3 Information Collected Automatically

Both our mobile app and website may collect:

- **Log Data**: IP address, browser type, access times, and referring URLs
- **Cookies and Similar Technologies**: On our website, we use cookies to maintain sessions and remember preferences (see Section 6)

---

## 2. How We Use Your Information

We use the information we collect to:

- **Provide Our Services**: Display maps, show your location, and deliver core functionality
- **Save Your Preferences**: Remember your favorite locations and settings (web app)
- **Improve Our Services**: Analyze usage patterns to enhance features and fix issues
- **Ensure Stability**: Monitor for errors, crashes, and performance issues
- **Communicate With You**: Send service-related notifications and respond to inquiries (web app users only)
- **Comply With Legal Obligations**: Meet applicable legal requirements

We do **not** use your information for:
- Selling to third parties
- Targeted advertising
- Building advertising profiles

---

## 3. How We Share Your Information

We do not sell your personal information. We may share information in the following limited circumstances:

### 3.1 Service Providers

We work with third-party service providers who help us operate our services:

| Provider | Purpose | Data Accessed |
|----------|---------|---------------|
| Amazon Web Services (AWS) | Hosting and infrastructure | Account data, usage logs |
| Sentry | Error tracking and crash reporting | Device info, crash logs, breadcrumbs |
| PostHog | Product analytics | Usage events, device identifiers, feature usage |

These providers are contractually obligated to protect your information and use it only for the services they provide to us.

### 3.2 Legal Requirements

We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).

### 3.3 Business Transfers

If ULLR Maps is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.

### 3.4 With Your Consent

We may share your information for other purposes with your explicit consent.

---

## 4. Data Retention

We retain your information only as long as necessary to provide our services and fulfill the purposes described in this policy:

- **Account Data**: Retained until you delete your account
- **Analytics Data**: Retained according to PostHog's default retention policies
- **Crash Reports**: Retained according to Sentry's default retention policies

You may request deletion of your data at any time (see Section 7).

---

## 5. Data Security

We implement appropriate technical and organizational measures to protect your information, including:

- Encryption of data in transit (TLS/SSL)
- Encryption of passwords using industry-standard hashing
- Access controls limiting who can view personal data
- Regular security assessments

However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.

---

## 6. Cookies and Tracking Technologies

### 6.1 Website

Our website uses cookies for:

- **Essential Cookies**: Required for authentication and core functionality
- **Preference Cookies**: Remember your settings and choices
- **Analytics Cookies**: Help us understand how visitors use our site

You can control cookies through your browser settings. Disabling cookies may affect some website functionality.

### 6.2 Mobile App

Our mobile app uses:

- **Device Identifiers**: To distinguish users for analytics
- **Local Storage**: To save your preferences offline

We do not use advertising identifiers (IDFA/GAID) or tracking pixels in our mobile app.

---

## 7. Your Privacy Rights

### 7.1 All Users

Regardless of where you are located, you have the right to:

- **Access**: Request a copy of the personal information we hold about you
- **Correction**: Request correction of inaccurate information
- **Deletion**: Request deletion of your personal information
- **Portability**: Receive your data in a portable format

To exercise these rights, contact us at info@ullrmaps.com.

### 7.2 European Economic Area (EEA) Residents - GDPR

If you are in the EEA, you have additional rights under the General Data Protection Regulation:

- **Legal Basis**: We process your data based on:
  - **Consent**: For location tracking (you can withdraw consent anytime via device settings)
  - **Contract**: To provide services you've requested
  - **Legitimate Interests**: For analytics and service improvement
- **Right to Object**: You may object to processing based on legitimate interests
- **Right to Restrict Processing**: You may request we limit how we use your data
- **Right to Lodge a Complaint**: You may file a complaint with your local data protection authority

**Data Controller**: ULLR Maps Ltd, British Columbia, Canada

### 7.3 California Residents - CCPA/CPRA

If you are a California resident, you have rights under the California Consumer Privacy Act:

- **Right to Know**: What personal information we collect, use, and disclose
- **Right to Delete**: Request deletion of your personal information
- **Right to Opt-Out**: Opt out of the "sale" or "sharing" of personal information
- **Right to Non-Discrimination**: We will not discriminate against you for exercising your rights

**We do not sell or share your personal information** for cross-context behavioral advertising purposes.

To exercise your California privacy rights, contact us at info@ullrmaps.com or use the methods described in Section 10.

In the past 12 months, we have collected the following categories of personal information:
- Identifiers (device ID, email, username)
- Geolocation data
- Internet activity information (usage data)

---

## 8. Children's Privacy

Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at info@ullrmaps.com, and we will delete such information.

---

## 9. International Data Transfers

Your information may be transferred to and processed in countries other than your country of residence, including Canada and the United States, where our servers and service providers are located.

If you are in the EEA, UK, or Switzerland, we ensure appropriate safeguards are in place for international transfers, including Standard Contractual Clauses approved by the European Commission.

---

## 10. Contact Us

If you have questions about this Privacy Policy or our privacy practices, please contact us:

**ULLR Maps Ltd**
British Columbia, Canada

**Email**: info@ullrmaps.com

**For privacy-related requests**: info@ullrmaps.com

We aim to respond to all requests within 30 days.

---

## 11. Changes to This Privacy Policy

We may update this Privacy Policy from time to time. We will notify you of any material changes by:

- Posting the new Privacy Policy on this page
- Updating the "Last Updated" date at the top
- Sending an email notification to registered users (for significant changes)

We encourage you to review this Privacy Policy periodically.

---

## 12. Consent

### Mobile App
By using our mobile app, you consent to the collection and use of information as described in this policy. Location tracking requires explicit permission through your device settings, which you can revoke at any time.

### Web Application
By creating an account on our website, you consent to the collection and use of your account information as described in this policy.

`;

export {privacyPolicy}